import { useMemo, useRef, useState } from 'react'
import { Timestamp } from 'firebase/firestore'

import {
  DocumentSignatorySignedBlockContent,
  DocumentSigningTemplateBlock,
  DocumentSigningTemplateBlockType,
} from '../../../../lib/db/db.types'
import {
  blockTypeToLabel,
  getSignedBlockValue,
} from '../SignDocumentFlow.utils'

import { BlockDisplay, PdfDisplay } from './PdfDisplay'
import { BlockContentDisplay } from './BlockContentDisplay'

type SignDocumentProps = {
  storageRef: string
  signatureName?: string
  signatureImage?: string
  blocks: DocumentSigningTemplateBlock[]
  acceptedBlocks: DocumentSignatorySignedBlockContent[]
  signatoryColor: string
  onAcceptBlock: (block: DocumentSignatorySignedBlockContent) => void
}

export function SignDocument({
  storageRef,
  signatureName,
  signatureImage,
  blocks,
  acceptedBlocks,
  signatoryColor,
  onAcceptBlock,
}: SignDocumentProps) {
  const pdfRef = useRef<{ scrollToBlock: (blockId: string) => void } | null>(
    null,
  )
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)

  const currentSignerBlocksDisplay = useMemo((): BlockDisplay[] => {
    return blocks.map((b): BlockDisplay => {
      const typeLabel = blockTypeToLabel[b.type]
      return {
        ...b,
        color: signatoryColor,
        content: (
          <BlockContentDisplay
            block={b}
            signedBlockContent={
              acceptedBlocks.find((block) => block.id === b.id)?.value
            }
          />
        ),
        label: typeLabel,
        width: b.size.width,
        height: b.size.height,
      }
    })
  }, [blocks, acceptedBlocks, signatoryColor])

  const handleBlockClick = (blockId: string) => {
    pdfRef.current?.scrollToBlock(blockId)
    setSelectedBlockId(blockId)
  }

  return (
    <div className="flex gap-4">
      <div className="w-64 space-y-4">
        <h3 className="font-medium">Required Fields</h3>
        <div className="space-y-2">
          {blocks.map((block, index) => {
            const displayBlock = currentSignerBlocksDisplay.find(
              (b) => b.id === block.id,
            )
            if (!displayBlock) return null
            return (
              <div
                key={block.id}
                onClick={() => handleBlockClick(block.id)}
                className={`w-full text-left p-2 rounded transition-color cursor-pointer ${
                  selectedBlockId === block.id
                    ? 'bg-blue-100'
                    : acceptedBlocks.some((b) => b.id === block.id)
                      ? 'bg-green-50'
                      : 'hover:bg-gray-100'
                }`}
              >
                <span className="text-sm font-medium">
                  {displayBlock.label}
                </span>
                <span className="text-xs text-gray-500 block">
                  Field {index + 1} • Page {block.page}
                </span>
                {selectedBlockId === block.id &&
                  !acceptedBlocks.some((b) => b.id === block.id) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onAcceptBlock({
                          id: block.id,
                          blockType: block.type,
                          insertedAt: Timestamp.now(),
                          value: getSignedBlockValue(block, {
                            currentDate: new Date(),
                            signatureName,
                            signatureImage,
                          }),
                        })
                        setSelectedBlockId(null)
                      }}
                      className="mt-2 w-full px-3 py-1 bg-blue-600 text-white rounded-sm text-sm hover:bg-blue-700"
                    >
                      {block.type === DocumentSigningTemplateBlockType.SIGNATURE
                        ? 'Sign'
                        : 'Accept'}
                    </button>
                  )}
                {acceptedBlocks.some((b) => b.id === block.id) && (
                  <div className="mt-2 text-sm">
                    <span className="text-green-600">
                      {block.type === DocumentSigningTemplateBlockType.SIGNATURE
                        ? 'Signed'
                        : 'Accepted'}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex-1 space-y-4">
        <h2 className="text-lg font-semibold">Your turn to sign</h2>
        <div className="border rounded">
          <PdfDisplay
            ref={pdfRef}
            readOnly
            storageRef={storageRef}
            blocks={currentSignerBlocksDisplay}
            scale={1}
            selectedBlockId={selectedBlockId}
          />
        </div>
      </div>
    </div>
  )
}
